// Shared scope pickers used by the Performance and Risk tabs.
//  • MultiPicker    — generic multi-select popover with search (accounts / baskets).
//  • GroupedPicker  — strategies grouped by basket, expand/collapse, group toggle.
// Empty selection = "all" (allLabel). Extracted verbatim from TwrPerformancePage so
// both tabs stay byte-identical in behaviour.
import React, { useState } from 'react';

export const Check = ({ state }) => ( // state: 'on' | 'some' | 'off'
  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] shrink-0 ${state === 'off' ? 'border-gray-400' : 'bg-brand-gold border-brand-gold text-white'}`}>
    {state === 'on' ? '✓' : state === 'some' ? '–' : ''}
  </span>
);

// items: [{ id, name, tag? }]. Empty selection = "all" (allLabel).
export const MultiPicker = ({ items, selected, onChange, allLabel, singular, width = 'min-w-[200px]' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const isAll = !selected || selected.length === 0;
  const label = isAll ? allLabel
    : selected.length === 1 ? (items.find((a) => a.id === selected[0])?.name || `1 ${singular}`)
    : `${selected.length} ${singular}s`;
  const toggle = (id) => {
    const set = new Set(selected || []);
    set.has(id) ? set.delete(id) : set.add(id);
    onChange([...set]);
  };
  const shown = q ? items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase())) : items;
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className={`text-sm bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 ${width} flex items-center justify-between gap-2 hover:border-brand-gold/50`}>
        <span className="truncate">{label}</span>
        <span className="text-gray-400 text-xs">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 w-[300px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
            {items.length > 8 && (
              <div className="p-1.5 border-b border-gray-100 dark:border-gray-800">
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${singular}s…`}
                  className="w-full text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5" />
              </div>
            )}
            <div className="max-h-[300px] overflow-y-auto scrollbar-thin p-1">
              <button onClick={() => onChange([])} className={`w-full text-left px-2.5 py-2 rounded text-sm flex items-center gap-2 ${isAll ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-300'} hover:bg-gray-100 dark:hover:bg-gray-800`}>
                <Check state={isAll ? 'on' : 'off'} />{allLabel}
              </button>
              <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
              {shown.length === 0 && <div className="px-2.5 py-2 text-xs text-gray-400">{items.length ? 'No matches' : 'None in scope'}</div>}
              {shown.map((a) => (
                <button key={a.id} onClick={() => toggle(a.id)} className="w-full text-left px-2.5 py-2 rounded text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
                  <Check state={(selected || []).includes(a.id) ? 'on' : 'off'} />
                  <span className="truncate">{a.name}</span>
                  {a.tag && <span className="ml-auto text-[10px] text-gray-400">{a.tag}</span>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// groups: [{ key, label, children:[{id, name}] }]. Empty selection = "all".
export const GroupedPicker = ({ groups, selected, onChange, allLabel, singular, width = 'min-w-[220px]' }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const selSet = new Set(selected || []);
  const isAll = selSet.size === 0;
  const label = isAll ? allLabel : `${selSet.size} ${singular}${selSet.size === 1 ? '' : 's'}`;
  const toggleOne = (id) => { const s = new Set(selSet); s.has(id) ? s.delete(id) : s.add(id); onChange([...s]); };
  const toggleGroup = (kids, allOn) => { const s = new Set(selSet); kids.forEach((c) => (allOn ? s.delete(c.id) : s.add(c.id))); onChange([...s]); };
  const toggleExp = (k) => setExpanded((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const shownGroups = groups
    .map((g) => ({ ...g, kids: q ? g.children.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())) : g.children }))
    .filter((g) => (q ? g.kids.length : g.children.length));
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className={`text-sm bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-1.5 ${width} flex items-center justify-between gap-2 hover:border-brand-gold/50`}>
        <span className="truncate">{label}</span>
        <span className="text-gray-400 text-xs">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 w-[320px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
            <div className="p-1.5 border-b border-gray-100 dark:border-gray-800">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${singular}s…`}
                className="w-full text-sm bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5" />
            </div>
            <div className="max-h-[320px] overflow-y-auto scrollbar-thin p-1">
              <button onClick={() => onChange([])} className={`w-full text-left px-2.5 py-2 rounded text-sm flex items-center gap-2 ${isAll ? 'text-brand-gold' : 'text-gray-700 dark:text-gray-300'} hover:bg-gray-100 dark:hover:bg-gray-800`}>
                <Check state={isAll ? 'on' : 'off'} />{allLabel}
              </button>
              <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
              {shownGroups.length === 0 && <div className="px-2.5 py-2 text-xs text-gray-400">No matches</div>}
              {shownGroups.map((g) => {
                const allOn = g.kids.length > 0 && g.kids.every((c) => selSet.has(c.id));
                const someOn = g.kids.some((c) => selSet.has(c.id));
                const isOpen = q ? true : expanded.has(g.key);
                return (
                  <div key={g.key}>
                    <div className="flex items-center gap-1 px-1 py-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
                      <button onClick={() => toggleExp(g.key)} className={`text-gray-400 text-[10px] w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}>▸</button>
                      <button onClick={() => toggleGroup(g.kids, allOn)} className="flex-1 flex items-center gap-2 text-left text-sm">
                        <Check state={allOn ? 'on' : someOn ? 'some' : 'off'} />
                        <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{g.label}</span>
                        <span className="ml-auto text-[10px] text-gray-400">{g.kids.length}</span>
                      </button>
                    </div>
                    {isOpen && g.kids.map((c) => (
                      <button key={c.id} onClick={() => toggleOne(c.id)} className="w-full text-left pl-8 pr-2.5 py-1.5 rounded text-[13px] flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
                        <Check state={selSet.has(c.id) ? 'on' : 'off'} />
                        <span className="truncate">{c.name}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
